import { useMemo } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { boschGray } from '../../../modules/theme/boschColors';
import { useApiData } from './useApiData';
import {
  buildChartPoints,
  ChartSeries,
  seriesColor,
} from './chartData';
import { ApiStatusBar } from './ApiStatusBar';

const useStyles = makeStyles(theme => ({
  chartBox: {
    padding: theme.spacing(1.5, 1, 0.5, 1),
    /* Pin a min-height so the ResponsiveContainer has room — the lib
     * collapses to 0 height when its parent has none, which is the
     * "I added a chart and nothing showed up" classic gotcha. */
    minHeight: 280,
  },
  empty: {
    padding: theme.spacing(2),
    color: boschGray[40],
    fontStyle: 'italic',
    fontSize: '0.85rem',
  },
}));

export type ApiBarChartProps = {
  title?: string;
  url?: string;
  inlineJson?: string;
  /** Path to the array of rows (each row will become a bar / group). */
  path?: string;
  /** Dot path on each row pointing at the X-axis label. */
  xKey: string;
  /** One or more numeric series to plot. Multiple series = grouped bars. */
  series: ChartSeries[];
  refreshSeconds?: number;
  /** When true, render stacked bars instead of grouped (side-by-side).
   *  Only matters when there are 2+ series. */
  stacked?: boolean;
};

/**
 * Bar chart powered by an API endpoint (or inline JSON for prototyping).
 * Multi-series support: each item in `series` becomes a colored
 * bar — either side-by-side (default) or stacked depending on the
 * `stacked` flag.
 *
 * Choice of Recharts over alternatives:
 *   - Recharts components compose naturally with React state, so
 *     widget-level config flows through declaratively (no need to
 *     manage a chart "instance" via refs).
 *   - `<ResponsiveContainer>` handles the grid-width responsive
 *     story for us — drop a chart at width 1/2 vs full and it
 *     resizes correctly without per-widget media queries.
 */
export const ApiBarChart = ({
  title,
  url,
  inlineJson,
  path,
  xKey,
  series,
  refreshSeconds,
  stacked = false,
}: ApiBarChartProps) => {
  const classes = useStyles();
  const { data, loading, error, lastUpdatedAt, refetch } = useApiData({
    url,
    inlineJson,
    path,
    refreshSeconds,
  });

  const points = useMemo(
    () =>
      buildChartPoints({
        rows: Array.isArray(data) ? data : [],
        xKey,
        series,
      }),
    [data, xKey, series],
  );

  return (
    <InfoCard title={title ?? 'Bar chart'} noPadding>
      {!url && !inlineJson ? (
        <Typography className={classes.empty}>
          Provide a URL or inline JSON to populate this chart.
        </Typography>
      ) : !Array.isArray(data) && data !== undefined ? (
        <Typography className={classes.empty}>
          Path <code>{path || '(root)'}</code> resolved to a {typeof data},
          not an array.
        </Typography>
      ) : points.length === 0 ? (
        <Typography className={classes.empty}>
          No rows to plot. Check the path / API response.
        </Typography>
      ) : (
        <div className={classes.chartBox}>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={points}
              margin={{ top: 8, right: 16, left: 0, bottom: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={boschGray[90]} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: boschGray[40] }}
                stroke={boschGray[80]}
              />
              <YAxis
                tick={{ fontSize: 11, fill: boschGray[40] }}
                stroke={boschGray[80]}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 4,
                  border: `1px solid ${boschGray[85]}`,
                }}
              />
              {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {series.map((s, idx) => (
                <Bar
                  key={s.dataKey}
                  dataKey={s.dataKey}
                  name={s.label ?? s.dataKey}
                  fill={seriesColor(s, idx)}
                  stackId={stacked ? 'stack' : undefined}
                  radius={[2, 2, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <ApiStatusBar
        loading={loading}
        error={error}
        lastUpdatedAt={lastUpdatedAt}
        onRefresh={url ? refetch : undefined}
        modeLabel={url ? 'Live API' : 'Inline JSON'}
      />
    </InfoCard>
  );
};
